import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchMe, login, logout, selectAuth } from "../features/auth/authSlice";
import { getLandingRoute } from "../app/navigation";

const NOT_STAFF_MESSAGE =
  "У этого пользователя нет доступа в CRM — он работает только на кассе. " +
  "Включите «Доступ в CRM» в «Управление → Пользователи».";

export default function LoginPage() {
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [formError, setFormError] = useState(() =>
    location.state?.reason === "not_staff" ? NOT_STAFF_MESSAGE : null,
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    try {
      await dispatch(login({ username, password })).unwrap();
      const user = await dispatch(fetchMe()).unwrap();
      // Пароль верный, но в CRM сотрудника не пускают (работает только на кассе). Без этой
      // проверки RequireStaff молча разлогинит его, и форма входа просто «мигнёт».
      if (user?.is_staff === false) {
        dispatch(logout());
        setFormError(NOT_STAFF_MESSAGE);
        return;
      }
      let target = location.state?.from?.pathname || "/";
      // «/» и «/sales» доступны не всем — отправляем роль на её стартовую страницу.
      if (target === "/" || target === "/sales") {
        target = getLandingRoute(user);
      }
      navigate(target, { replace: true });
    } catch (err) {
      setFormError(err?.status === 401 ? "Неверный логин или пароль." : err?.message || "Не удалось войти.");
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoRow}>
          <span style={styles.logoAuf}>AUF</span>
          <span style={styles.logoCrm}>CRM</span>
        </div>
        <h1 style={styles.title}>Добро пожаловать</h1>
        <p style={styles.subTitle}>Войдите в свой аккаунт</p>

        <form onSubmit={onSubmit} style={styles.form}>
          <label style={styles.label}>
            Username
            <input
              style={styles.input}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />
          </label>

          <label style={styles.label}>
            Password
            <input
              style={styles.input}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              autoComplete="current-password"
              required
            />
          </label>

          {formError && <div style={styles.error}>{formError}</div>}

          <button style={styles.button} type="submit" disabled={auth.status === "loading"}>
            {auth.status === "loading" ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <div style={styles.hint}>
          API base: <code>{process.env.REACT_APP_API_BASE_URL || "(same origin)"}</code>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    background: "var(--c-bg)",
    padding: 16,
  },
  card: {
    width: "100%",
    maxWidth: 400,
    background: "var(--c-card)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    padding: 28,
    color: "var(--c-text)",
    boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
  },
  logoRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 24,
  },
  logoAuf: {
    fontSize: 26,
    fontWeight: 900,
    color: "var(--c-accent)",
    letterSpacing: "-0.5px",
  },
  logoCrm: {
    fontSize: 16,
    fontWeight: 600,
    color: "var(--c-text-dim)",
    letterSpacing: "2px",
    textTransform: "uppercase",
  },
  title: { margin: "0 0 4px", fontSize: 22, fontWeight: 800, color: "var(--c-text)" },
  subTitle: { margin: "0 0 20px", fontSize: 15, color: "var(--c-text-muted)" },
  form: { display: "grid", gap: 14 },
  label: { display: "grid", gap: 6, fontSize: 14, fontWeight: 600, color: "var(--c-text-sub)" },
  input: {
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 16,
    fontFamily: "inherit",
  },
  button: {
    marginTop: 4,
    padding: "11px 16px",
    borderRadius: 99,
    border: "none",
    background: "var(--c-btn)",
    color: "var(--c-btn-text)",
    fontWeight: 700,
    fontSize: 16,
    cursor: "pointer",
  },
  error: {
    padding: "10px 14px",
    borderRadius: 8,
    background: "var(--c-danger-dim)",
    border: "1px solid var(--c-danger)",
    color: "var(--c-danger)",
    fontSize: 15,
  },
  hint: { marginTop: 16, fontSize: 13, color: "var(--c-text-dim)" },
};


